import getConfig from 'next/config';

import {GtmEvents, GtmPageTypes} from '~/src/utilities/constants/tracking';
import {ContentType} from '~/src/utilities/constants/types';
import {getCookieJson, getCookie} from '~/src/utilities/helpers/cookie';
import {Cookie} from '~/src/utilities/constants/cookie';
import {userIsLoggedIn} from '~/src/utilities/helpers/account';
import {loadExternalDependency} from '~/src/utilities/helpers/dependency';
import {getCategoryBy} from '~/src/utilities/feed/sitemap';
import {Logger} from '~/src/utilities/helpers/log';
import {SitemapEntry} from '~/src/types/data/sitemap';
import {normalizeDate, utcToIso8601} from '../helpers/date';

const {publicRuntimeConfig: config} = getConfig();

declare global {
    interface Window {
        dataLayer: TrackingObject[];
    }
}

interface TrackingObject {
    app_cid?: string;
    article_author?: string;
    article_id?: string;
    article_length?: number;
    article_modified_datetime?: string;
    article_nb_ads?: number;
    article_nb_galleries?: number;
    article_nb_pictures?: number;
    article_nb_videos?: number;
    article_position?: number;
    article_publish_datetime?: string;
    article_title?: string;
    article_type?: string;
    channel_level1?: string;
    channel_level2?: string;
    channel_unity?: string;
    event?: string;
    event_action?: string;
    event_category?: string;
    event_label?: string;
    event_type?: string;
    gallery_id?: string;
    gallery_nb_images?: number;
    gallery_position?: number;
    gallery_swipe_direction?: string;
    gallery_title?: string;
    interaction_name?: string;
    interaction_source?: string;
    listening_type?: string;
    login_error?: string;
    login_result?: string;
    loginoverlay_count?: number;
    nav_click_category_id?: string;
    nav_click_level?: number;
    nav_click_name?: string;
    nav_status_level1?: string;
    nav_status_level2?: string;
    nav_status_level3?: string;
    page_canonical_url?: string;
    page_title?: string;
    page_type?: string;
    picture_id?: string;
    picture_title?: string;
    platform_name?: string;
    platform_type?: string;
    push_notification_content?: string;
    quiz_answer?: string;
    quiz_destination_url?: string;
    quiz_name?: string;
    quiz_question?: string;
    rating_id?: string;
    rating_text?: string;
    rating_type?: string;
    right_left_hand?: string;
    sitesearch_term?: string;
    user_feed_category?: string;
    user_feed_id?: string;
    user_feed_step?: string;
    user_id?: string;
    user_login_status?: string;
    video_aspect_ratio?: string;
    video_author?: string;
    video_content_category?: string;
    video_id?: string;
    video_length?: string;
    video_modified_datetime?: string;
    video_nb_ads?: number;
    video_player?: string;
    video_position?: number;
    video_progress?: string;
    video_progress_method?: string;
    video_publish_datetime?: string;
    video_title?: string;
    video_url?: string;
    video_variety?: string;
    video_swipe_direction?: string;
    is_automatic?: string;
    track_number?: number;
    track_channel?: string;
    track_name?: string;
}

const getUserId = () => {
    const sessionUser = getCookieJson<any>(Cookie.SESSION);
    return sessionUser?.userId;
};

const loadGtmDependency = async () => {
    try {
        await loadExternalDependency({src: config.scripts.gtm});
    } catch (error) {
        Logger.info(`[${error.name}][gtm] Error while loading gtm dependency`, {error});
    }
};

const countElements = (elements: any[]) => {
    let counts: {[key: string]: number} = {};
    elements.forEach((element) => {
        counts[element.type] = counts[element.type] ? counts[element.type] + 1 : 1;
    });
    return counts;
};

const getCountsByType = (data: any) => {
    let typeCounts: {[key: string]: number} = {};

    switch (data.type) {
        case ContentType.ARTICLE:
        case ContentType.LIFESTYLE:
            typeCounts = countElements(data?.content?.article?.elements || []);
            break;
        case ContentType.TICKER:
            (data?.content?.events || []).forEach((tickerEvent: any) => {
                typeCounts = countElements(tickerEvent?.content?.elements || []);
            });
            break;
        case ContentType.SLIDESHOW:
            typeCounts.slideshow = 1;
            break;
        default:
            break;
    }

    return typeCounts;
};

const getAuthors = (data: any) => (data?.content?.meta?.authors || []).filter((author: any) => author.type === 'user').map((author: any) => author.name);

const getDocumentTitle = () => new Promise<string>((resolve) => setTimeout(() => resolve(document.title), 0));

const getContentPageData = (data: any): TrackingObject => {
    const typeCounts = getCountsByType(data);
    const categories = (data?.content?.meta?.mainCategoryFullUrlPath || '').split('/');
    const published = data?.content?.meta?.published || new Date().toISOString();
    const modified = data?.content?.meta?.updated || published;

    return {
        article_author: getAuthors(data),
        article_id: data.id,
        article_modified_datetime: utcToIso8601(modified),
        article_nb_ads: typeCounts.ad || 0,
        article_nb_galleries: typeCounts.slideshow || 0,
        article_nb_videos: typeCounts.videocms || 0,
        article_publish_datetime: utcToIso8601(published),
        article_title: data?.content?.meta?.teaser?.default?.title || '',
        article_type: data.type,
        channel_level1: categories[0],
        channel_level2: categories[1]
    };
};

const getCategoryPageData = (data: any): TrackingObject => {
    const categories = (data?.category?.fullUrlPath || '').split('/');
    return {
        channel_level1: categories[0],
        channel_level2: categories[1]
    };
};

const convertTimeFormat = (duration: string | number) => {
    duration = Number(duration);
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor((duration % 3600) % 60);

    const hoursFormatted = `0${hours}`.slice(-2);
    const minutesFormatted = `0${minutes}`.slice(-2);
    const secondsFormatted = `0${seconds}`.slice(-2);

    return `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`;
};

const trackPageview = async (type: string, data: any) => {
    const title = await getDocumentTitle();
    let dataLayerObject: TrackingObject = {
        event: type,
        page_canonical_url: data.canonicalUrl,
        page_title: title,
        page_type: type.split('_')[0],
        platform_name: config.tenantName,
        platform_type: 'web',
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous',
        app_cid: getCookie(Cookie.APP_CID) || undefined
    };

    switch (type) {
        case GtmPageTypes.ARTICLE:
        case GtmPageTypes.NEWSTICKER:
        case GtmPageTypes.SLIDESHOW:
        case GtmPageTypes.VIDEO:
            dataLayerObject = {...dataLayerObject, ...getContentPageData(data)};
            break;
        case GtmPageTypes.CHANNEL:
        case GtmPageTypes.FRONT:
            dataLayerObject = {...dataLayerObject, ...getCategoryPageData(data)};
            break;
        default:
            break;
    }

    return {
        ...dataLayerObject,
        nav_status_level1: data.navStatusLevel1,
        nav_status_level2: data.navStatusLevel2,
        nav_status_level3: data.navStatusLevel3
    };
};

const trackFeatureOverlay = (eventName: string): TrackingObject => {
    let featureModalCount = 0;

    try {
        featureModalCount = Number(localStorage.getItem('featureModalCount'));
    } catch (error) {
        Logger.info(`[${error.name}][gtm] Browser does not support LocalStorage API`, {error});
    }

    return {
        event: eventName,
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous',
        loginoverlay_count: featureModalCount,
        platform_name: config.tenantName,
        platform_type: 'web'
    };
};

const trackVideo = async (eventName: string, data: any): Promise<TrackingObject> => {
    const title = await getDocumentTitle();
    const categories = (data?.content?.meta?.mainCategoryFullUrlPath || '').split('/');

    return {
        event: eventName,
        page_title: title,
        channel_level1: categories[0],
        channel_level2: categories[1],
        platform_type: 'web',
        platform_name: config.tenantName,
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous',
        video_aspect_ratio: data.aspect_ratio,
        video_author: getAuthors(data),
        video_content_category: Object.values(data.meta.content_category).join(','),
        video_position: data.position || 1,
        video_id: data.video_id,
        video_modified_datetime: utcToIso8601(normalizeDate(data.date_updated || data.date)),
        video_length: convertTimeFormat(data.duration),
        video_publish_datetime: utcToIso8601(normalizeDate(data.date)),
        video_title: data.title,
        video_url: data.url_high,
        video_progress: data.progress,
        video_progress_method: data.progressMethod
    };
};

const trackVideoSlider = (eventName: string, params: any): TrackingObject => {
    const categories = (params?.content?.mainCategoryFullUrlPath || '').split('/');
    return {
        event: eventName,
        channel_level1: categories[0],
        channel_level2: categories[1],
        video_title: params.content.title,
        video_url: params.content.url,
        video_id: params.id,
        video_position: params.sortID,
        channel_unity: params.pointerCategoryID
    };
};

const trackGallery = async (eventName: string, data: any): Promise<TrackingObject> => {
    const title = await getDocumentTitle();
    return {
        event: eventName,
        gallery_id: data.id,
        gallery_nb_images: data.slideshowLength,
        gallery_swipe_direction: data.direction || undefined,
        gallery_title: data.title || undefined,
        gallery_position: data.position || 1,
        page_title: title,
        picture_id: data.pictureId,
        picture_title: data.pictureTitle,
        platform_type: 'web',
        platform_name: config.tenantName,
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous'
    };
};

const trackLoginAttempt = async (eventName: string): Promise<TrackingObject> => {
    const title = await getDocumentTitle();
    return {
        event: eventName,
        page_title: title,
        platform_type: 'web',
        platform_name: config.tenantName,
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous'
    };
};

const trackInteraction = async (params: any): Promise<TrackingObject> => {
    const title = await getDocumentTitle();

    return {
        event: GtmEvents.INTERACTION,
        platform_type: 'web',
        platform_name: config.tenantName,
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous',
        interaction_name: params.interactionType,
        interaction_source: params.interactionSource,
        page_title: title,
        article_id: params.contentId
    };
};

const trackInteractionRating = async ({id, title, authors, fullUrlPath, type, question, rating}: any): Promise<TrackingObject> => {
    const categories = fullUrlPath?.split('/');
    const isVideo = type === 'video';

    return {
        event: GtmEvents.INTERACTION,
        platform_type: 'web',
        platform_name: config.tenantName,
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous',
        page_title: title,
        interaction_source: 'rating',
        rating_type: type,
        interaction_name: rating ? 'like' : 'unlike',
        rating_text: question.label,
        rating_id: question.questionId,
        article_id: !isVideo && id ? id : undefined,
        video_id: isVideo && id ? id : undefined,
        article_author: authors.filter((author: any) => author.type === 'user').map((author: any) => author.name),
        article_title: title,
        article_type: type,
        channel_level1: categories && categories[0],
        channel_level2: categories && categories[1]
    };
};

const trackNavigation = async (eventName: string, params: any): Promise<TrackingObject> => {
    return {
        event: eventName,
        nav_click_level: params.level,
        nav_click_name: params.label,
        nav_click_category_id: params.categoryId
    };
};

const trackProfile = async (eventName: string): Promise<TrackingObject> => {
    const title = await getDocumentTitle();

    return {
        event: eventName,
        page_title: title,
        page_type: 'profile',
        platform_type: 'web',
        platform_name: config.tenantName,
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous'
    };
};

const trackWidgetItem = async (eventName: string, params: any) => {
    const title = await getDocumentTitle();

    return {
        event: eventName,
        article_id: params.id,
        article_position: params.position,
        article_title: params.title,
        page_title: title,
        page_type: params.pageType?.split('_')[0],
        platform_name: config.tenantName,
        platform_type: 'web',
        user_id: getUserId(),
        user_login_status: userIsLoggedIn() ? 'logged in' : 'anonymous'
    };
};

const trackGtm = async (trackingEvent: string, params: any) => {
    loadGtmDependency();

    let trackingObject: TrackingObject = {};

    switch (trackingEvent) {
        case GtmEvents.PAGEVIEW:
            trackingObject = await trackPageview(params.pageType, params.data);
            break;
        case GtmEvents.LOGIN_OVERLAY_DISPLAY:
        case GtmEvents.LOGIN_OVERLAY_CLOSE:
        case GtmEvents.LOGIN_OVERLAY_REGISTER:
        case GtmEvents.LOGIN_OVERLAY_LOGIN:
        case GtmEvents.LOGIN_OVERLAY_INFORMATION:
        case GtmEvents.FEATURE_OVERLAY_INFORMATION:
        case GtmEvents.FEATURE_OVERLAY_LOGIN:
        case GtmEvents.FEATURE_OVERLAY_REGISTER:
        case GtmEvents.FEATURE_OVERLAY_DISPLAY:
        case GtmEvents.FEATURE_OVERLAY_CLOSE:
            trackingObject = trackFeatureOverlay(trackingEvent);
            break;
        case GtmEvents.VIDEO_AD_START:
        case GtmEvents.VIDEO_AD_COMPLETE:
        case GtmEvents.VIDEO_PLAY:
        case GtmEvents.VIDEO_RESUME:
        case GtmEvents.VIDEO_PROGRESS:
        case GtmEvents.VIDEO_SEEK:
        case GtmEvents.VIDEO_COMPLETE:
            trackingObject = await trackVideo(trackingEvent, params);
            break;
        case GtmEvents.VIDEO_SLIDER:
            trackingObject = trackVideoSlider(trackingEvent, params);
            break;
        case GtmEvents.GALLERY_SWIPE:
        case GtmEvents.GALLERY_FULLSCREEN:
            trackingObject = await trackGallery(trackingEvent, params);
            break;
        case GtmEvents.LOGIN_ATTEMPT:
            trackingObject = await trackLoginAttempt(trackingEvent);
            break;
        case GtmEvents.INTERACTION:
            trackingObject = await trackInteraction(params);
            break;
        case GtmEvents.INTERACTION_RATING:
            trackingObject = await trackInteractionRating(params);
            break;
        case GtmEvents.NAVIGATION:
            trackingObject = await trackNavigation(trackingEvent, params);
            break;
        case GtmEvents.PROFILE_EDIT_CLICK:
            trackingObject = await trackProfile(trackingEvent);
            break;
        case GtmEvents.WIDGET_MOST_COMMENTED:
        case GtmEvents.WIDGET_MOST_POPULAR:
        case GtmEvents.WIDGET_RECENTLY_COMMENTED:
        case GtmEvents.WIDGET_COMMENT_HISTORY:
            trackingObject = await trackWidgetItem(trackingEvent, params);
            break;
        default:
            break;
    }

    trackingObject.event_type = trackingObject.event?.split('_')[0];

    window.dataLayer.push(trackingObject);
};

const buildNavStatusLevels = (siteArea: string, categoryId: string, categories: SitemapEntry[] = []) => {
    const levels = [];

    let current = getCategoryBy(categories, {categoryId});
    while (current) {
        if (current?.visibility?.inMenu) {
            levels.unshift(current.name);
        }
        current = getCategoryBy(categories, {categoryId: current.parent});
    }

    return {
        navStatusLevel1: siteArea,
        navStatusLevel2: levels[0],
        navStatusLevel3: levels[1]
    };
};

const createNavigationTracker = (level: number, label: string, categoryId: string | number) => () => {
    categoryId = String(categoryId);
    trackGtm(GtmEvents.NAVIGATION, {
        level,
        label,
        categoryId
    });
};

const createTopItemTracker = (event: string, pageType: string, id: string, position: number, title: string) => () => {
    trackGtm(event, {pageType, id, position, title});
};

export {trackGtm, buildNavStatusLevels, createNavigationTracker, createTopItemTracker};
